"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Calendar, Clock, DollarSign } from "lucide-react";
import type { QuickBooksInvoice } from "@/lib/types/quickbooks-unified";
import { PaymentStatusBadge } from "./InvoiceBadges";
import { formatCurrency, formatDate, formatDateTime } from "../utils/invoiceFormatters";

interface InvoiceOverviewTabProps {
  invoice?: QuickBooksInvoice;
}

/**
 * Overview tab component for invoice details
 * Displays invoice information, financial summary, and metadata
 */
export const InvoiceOverviewTab: React.FC<InvoiceOverviewTabProps> = ({ invoice }) => {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Invoice Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Invoice Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <FileText className="h-3.5 w-3.5" />
                  Invoice Number
                </div>
                <div className="text-lg font-semibold">{invoice?.DocNumber || "N/A"}</div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <Calendar className="h-3.5 w-3.5" />
                  Invoice Date
                </div>
                <div className="text-base md:text-lg font-medium">
                  {formatDate(invoice?.TxnDate)}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <Calendar className="h-3.5 w-3.5" />
                  Due Date
                </div>
                <div className="text-base md:text-lg font-semibold text-card-foreground">
                  {formatDate(invoice?.DueDate)}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <Clock className="h-3.5 w-3.5" />
                  Payment Terms
                </div>
                <div className="text-base md:text-lg font-medium">
                  {invoice?.SalesTermRef?.name || "N/A"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Financial Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <div className="text-sm font-medium text-gray-600 mb-2">Total Amount</div>
                <div className="text-4xl font-bold text-green-600">
                  {formatCurrency(invoice?.TotalAmt)}
                </div>
              </div>

              <div className="text-center p-6 bg-red-50 rounded-lg">
                <div className="text-sm font-medium text-gray-600 mb-2">Balance Due</div>
                <div className="text-4xl font-bold text-red-600">
                  {formatCurrency(invoice?.Balance)}
                </div>
              </div>

              <div className="text-center p-6 bg-blue-50 rounded-lg sm:col-span-1">
                <div className="text-sm font-medium text-gray-600 mb-2">Payment Status</div>
                <div className="text-lg md:text-xl">
                  <PaymentStatusBadge balance={invoice?.Balance} total={invoice?.TotalAmt} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metadata */}
      {invoice?.MetaData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Created:</span>
                <div className="font-medium">{formatDateTime(invoice.MetaData.CreateTime)}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Last Updated:</span>
                <div className="font-medium">
                  {formatDateTime(invoice.MetaData.LastUpdatedTime)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
